/**
 * gammaPromotionProfile.ts — MER Algo Footprints v7.1B — Phase 6.7
 *
 * Versioned Gamma promotion profile model and registry.
 *
 * ── Purpose ───────────────────────────────────────────────────────────────────
 * A GammaPromotionProfile represents a candidate Gamma flag configuration that
 * has been proposed for production use. Profiles are:
 *   - explicitly versioned and identified
 *   - strictly validated before storage
 *   - traceable to source recommendations
 *   - reversible at any time by activating the baseline profile
 *
 * ── Rollout lifecycle ────────────────────────────────────────────────────────
 *   inactive  → shadow  → limited  → active  → retired
 *   OR at any point:  → rollback (activate baseline, retire candidate)
 *
 * ── Immutability contract ────────────────────────────────────────────────────
 * The BASELINE profile is immutable and cannot be overwritten.
 * No code path in this module mutates GAMMA_INFLUENCE_FLAGS.
 *
 * Placement: src/domains/market/engines/gamma/gammaPromotionProfile.ts
 */

import type { GammaInfluenceFlags } from './gammaInfluenceFlags';
import { GAMMA_INFLUENCE_FLAGS }    from './gammaInfluenceFlags';

// ─── Rollout status ───────────────────────────────────────────────────────────

export type RolloutStatus =
  | 'inactive'   // defined but not applied anywhere
  | 'shadow'     // applied in shadow mode: does not affect live scoring
  | 'limited'    // applied to a defined limited scope only
  | 'active'     // applied to all eligible evaluations
  | 'retired';   // superseded or withdrawn

// ─── Rollout scope ────────────────────────────────────────────────────────────

export interface RolloutScope {
  /**
   * If set, only symbols in this list receive candidate flags.
   * Empty or absent = applies to all symbols (for 'active' mode).
   * Required to be non-empty for 'limited' status.
   */
  symbols?:    string[];
  /**
   * If set, only setup types matching any of these substrings receive candidate flags.
   */
  setupTypes?: string[];
  /**
   * Human-readable note about the scope restriction.
   */
  note?:       string;
}

// ─── Promotion profile ────────────────────────────────────────────────────────

export interface GammaPromotionProfile {
  /** Stable machine-readable ID. Convention: 'baseline' | 'v<N>_<descriptor>' */
  id:                  string;
  /** Semantic version label, e.g. '1.0.0' */
  version:             string;
  /** Human-readable label. */
  label:               string;
  /** Detailed notes about what this profile changes and why. */
  notes:               string;
  /** Current rollout status. */
  status:              RolloutStatus;
  /** ID of the profile this was derived from (null for baseline). */
  parentProfileId:     string | null;
  /** IDs of Phase 6.6 recommendations that motivated this profile. */
  sourceRecommendationIds: string[];
  /** The Gamma flag configuration for this profile. */
  flags:               GammaInfluenceFlags;
  /** Rollout scope constraints. Ignored for 'inactive'/'shadow'/'active'. */
  scope:               RolloutScope;
  /** Unix ms when this profile was created. */
  createdAt:           number;
  /** Whether this profile has passed validation. */
  validated:           boolean;
  /** Whether this profile is the immutable baseline. */
  isBaseline:          boolean;
}

// ─── Immutable baseline ───────────────────────────────────────────────────────

/**
 * The production baseline profile.
 * Exactly mirrors GAMMA_INFLUENCE_FLAGS.
 * Immutable — cannot be overwritten or retired.
 */
export const BASELINE_PROFILE: GammaPromotionProfile = Object.freeze({
  id:                      'baseline',
  version:                 '1.0.0',
  label:                   'Production baseline',
  notes:                   'Immutable baseline reflecting current production defaults. Both Gamma influence flags are OFF. No confidence or ranking adjustments applied.',
  status:                  'active' as RolloutStatus,
  parentProfileId:         null,
  sourceRecommendationIds: [],
  flags:                   Object.freeze({ ...GAMMA_INFLUENCE_FLAGS }),
  scope:                   Object.freeze({}),
  createdAt:               0,
  validated:               true,
  isBaseline:              true,
});

// ─── Profile registry (in-memory) ────────────────────────────────────────────

const _registry = new Map<string, GammaPromotionProfile>();
_registry.set('baseline', BASELINE_PROFILE);

/**
 * Registers a validated promotion profile.
 * Throws if:
 *   - id is 'baseline' (baseline is immutable)
 *   - profile has not been validated
 *   - a profile with the same id already exists and has different content
 */
export function registerProfile(profile: GammaPromotionProfile): void {
  if (profile.id === 'baseline') {
    throw new Error('Cannot overwrite the immutable baseline profile.');
  }
  if (!profile.validated) {
    throw new Error(`Profile '${profile.id}' must be validated before registration.`);
  }
  const existing = _registry.get(profile.id);
  if (existing && existing.createdAt !== profile.createdAt) {
    throw new Error(`Profile '${profile.id}' already exists with different content. Use a new ID or version.`);
  }
  _registry.set(profile.id, profile);
}

/**
 * Returns a profile by ID. Returns null if not found.
 */
export function getProfile(id: string): GammaPromotionProfile | null {
  return _registry.get(id) ?? null;
}

/**
 * Returns all registered profiles (including baseline).
 */
export function getAllProfiles(): GammaPromotionProfile[] {
  return Array.from(_registry.values());
}

/**
 * Returns the current candidate profile — the highest-priority non-baseline,
 * non-retired profile regardless of status (shadow/limited/active/inactive).
 *
 * Priority order: active > limited > shadow > inactive
 * When none exists, returns the baseline profile.
 *
 * Used by the promotion orchestrator to find the candidate to route against.
 */
export function getCurrentCandidate(): GammaPromotionProfile {
  const priority: RolloutStatus[] = ['active', 'limited', 'shadow', 'inactive'];
  for (const status of priority) {
    const found = Array.from(_registry.values()).find(
      p => !p.isBaseline && p.status === status
    );
    if (found) return found;
  }
  return BASELINE_PROFILE;
}
/**
 * Returns the currently active profile.
 * There should be at most one 'active' non-baseline profile.
 * Falls back to baseline when no candidate is active.
 */
export function getActiveProfile(): GammaPromotionProfile {
  const candidates = Array.from(_registry.values()).filter(
    p => p.status === 'active' && !p.isBaseline
  );
  if (candidates.length > 1) {
    // More than one active — safety fallback: return baseline
    console.warn('[GammaPromotion] Multiple active profiles found — falling back to baseline.');
    return BASELINE_PROFILE;
  }
  return candidates[0] ?? BASELINE_PROFILE;
}

/**
 * Updates the status of a registered profile.
 * Cannot change baseline status.
 * Returns the updated profile.
 */
export function updateProfileStatus(
  id:        string,
  newStatus: RolloutStatus,
): GammaPromotionProfile {
  if (id === 'baseline') {
    throw new Error('Cannot change the status of the baseline profile.');
  }
  const profile = _registry.get(id);
  if (!profile) throw new Error(`Profile '${id}' not found.`);
  const updated = { ...profile, status: newStatus };
  _registry.set(id, updated);
  return updated;
}

/**
 * Retires all non-baseline active/limited/shadow profiles and returns to baseline.
 * This is the canonical rollback operation.
 * Returns the list of profile IDs that were retired.
 */
export function rollbackToBaseline(): string[] {
  const retired: string[] = [];
  for (const [id, profile] of _registry.entries()) {
    if (profile.isBaseline) continue;
    if (['active','limited','shadow'].includes(profile.status)) {
      _registry.set(id, { ...profile, status: 'retired' });
      retired.push(id);
    }
  }
  return retired;
}

/**
 * Clears all non-baseline profiles (for testing).
 */
export function clearNonBaselineProfiles(): void {
  for (const [id] of _registry.entries()) {
    if (id !== 'baseline') _registry.delete(id);
  }
}

/**
 * Builds a new promotion profile (not yet registered or validated).
 * Caller must validate via validateProfile() before registering.
 */
export function buildProfile(
  id:                      string,
  label:                   string,
  version:                 string,
  notes:                   string,
  flags:                   GammaInfluenceFlags,
  opts?: {
    parentProfileId?:        string;
    sourceRecommendationIds?: string[];
    scope?:                  RolloutScope;
    status?:                 RolloutStatus;
  },
): GammaPromotionProfile {
  return {
    id,
    version,
    label,
    notes,
    status:                  opts?.status              ?? 'inactive',
    parentProfileId:         opts?.parentProfileId     ?? 'baseline',
    sourceRecommendationIds: opts?.sourceRecommendationIds ?? [],
    flags,
    scope:                   opts?.scope               ?? {},
    createdAt:               Date.now(),
    validated:               false,   // must be validated before registration
    isBaseline:              false,
  };
}
